import { useEffect } from "react";
import { FaCalendarAlt, FaCheckCircle, FaTags, FaUser } from "react-icons/fa";
import { Link, useParams } from "react-router-dom";
import heroImage from "../assets/contacthero.webp";
import { projects } from "../data/Projects";

function ProjectDetail() {
    const { projectTitle } = useParams();

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Find project by URL-friendly title
    const project = projects.find(
        (p) => p.title.replace(/\s+/g, "-").toLowerCase() === projectTitle
    );

    if (!project) {
        return <p className="text-center mt-20">Project not found</p>;
    }

    return (
        <div>
            {/* Hero Section */}
            <section
                className="relative w-full h-[30vh] md:h-[40vh] bg-cover bg-center"
                style={{ backgroundImage: `url(${heroImage})` }}
            >
                <div className="absolute inset-0 bg-black bg-opacity-75"></div>
                <div className="relative z-10 flex flex-col items-start justify-center h-full px-6 md:px-12 max-w-6xl mx-auto text-white">
                    <h1 className="text-4xl md:text-5xl font-bold mb-4">
                        {project.title}
                    </h1>
                    <div className="flex items-center text-sm md:text-base space-x-2 text-gray-200">
                        <Link to="/" className="hover:text-white cursor-pointer">
                            Home
                        </Link>
                        <span>/</span>
                        <Link
                            to="/projects"
                            className="text-gray-300 hover:text-white cursor-pointer"
                        >
                            Projects
                        </Link>
                        <span>/</span>
                        <span className="text-gray-300 underline">{project.title}</span>
                    </div>
                </div>
            </section>

            {/* Project Details Section */}
            <section className="py-16 bg-gray-50">
                <div className="max-w-6xl mx-auto px-6">
                    {/* Image List */}
                    <div className="grid gap-6 md:grid-cols-3 mb-16 relative">
                        {/* First 3 images */}
                        {project.images.slice(0, 3).map((img, index) => (
                            <div
                                key={index}
                                className="overflow-hidden rounded-lg shadow-md"
                            >
                                <img
                                    src={img}
                                    alt={`${project.title} ${index + 1}`}
                                    loading="lazy"
                                    className="w-full h-80 object-cover transform transition-transform duration-500 hover:scale-110"
                                />
                            </div>
                        ))}

                        {/* Last image full width */}
                        <div className="relative md:col-span-3 overflow-hidden">
                            <img
                                src={project.images[3]}
                                alt={`${project.title} 4`}
                                loading="lazy"
                                className="w-full h-[550px] object-cover rounded-none shadow-md transform transition-transform duration-500 hover:scale-110"
                            />

                            {/* Floating Info Card */}
                            <div className="absolute bottom-0 right-0 bg-white rounded-none shadow-lg w-full md:w-[25%]">
                                <div className="bg-secondary text-primary px-4 py-2">
                                    <h1 className="font-bold text-lg">
                                        Project Information
                                    </h1>
                                </div>
                                <div className="space-y-8 mt-4 p-6">
                                    <div className="flex flex-row items-center text-gray-700">
                                        <FaUser className="text-primary mr-3" />
                                        <div className="flex flex-col">
                                            <span><strong>Client:</strong></span>
                                            <span>{project.client}</span>
                                        </div>
                                    </div>

                                    <div className="flex flex-row items-center text-gray-700">
                                        <FaTags className="text-primary mr-3" />
                                        <div className="flex flex-col">
                                            <span><strong>Category:</strong></span>
                                            <span>{project.category}</span>
                                        </div>
                                    </div>

                                    <div className="flex flex-row items-center text-gray-700">
                                        <FaCalendarAlt className="text-primary mr-3" />
                                        <div className="flex flex-col">
                                            <span><strong>Date Started:</strong></span>
                                            <span>{project.dateStarted}</span>
                                        </div>
                                    </div>

                                    <div className="flex flex-row items-center text-gray-700">
                                        <FaCheckCircle className="text-primary mr-3" />
                                        <div className="flex flex-col">
                                            <span><strong>Date Completed:</strong></span>
                                            <span>{project.dateCompleted}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-800">
                        {project.title}
                    </h2>

                    {/* Description */}
                    <p className="text-gray-600 mb-10">{project.description}</p>
                </div>
            </section>
        </div>
    );
}

export default ProjectDetail;
